"use client";

import { createContext, useContext } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { InvestorPlantMapPoint, SupportedLocale, TenantPortalContextReadModel } from "@blizhub/shared";
import type { CustomerThemeMode } from "./customer-shell";
import type { SessionProfileClaims } from "./session-profile";

export type CustomerPortalSidePanel = {
  actions?: ReactNode;
  ariaLabel?: string;
  children: ReactNode;
  helpTextKey: string;
  onClose: () => void;
  showCloseButton?: boolean;
  size?: "default" | "wide";
  title: ReactNode;
  uiId: string;
};

export type CustomerPortalMobileBackAction = {
  onBack: () => void;
  title?: ReactNode;
};

export type CustomerPortalContextValue = {
  accentColor: string | null;
  locale: SupportedLocale;
  mobileBackAction: CustomerPortalMobileBackAction | null;
  portalSidePanel: CustomerPortalSidePanel | null;
  sessionProfile: SessionProfileClaims;
  selectedProductionUnit: InvestorPlantMapPoint | null;
  setLocale: (locale: SupportedLocale) => void;
  setMobileBackAction: Dispatch<SetStateAction<CustomerPortalMobileBackAction | null>>;
  setPortalSidePanel: Dispatch<SetStateAction<CustomerPortalSidePanel | null>>;
  setSelectedProductionUnit: (point: InvestorPlantMapPoint | null) => void;
  setThemeMode: (themeMode: CustomerThemeMode) => void;
  tenantContext: TenantPortalContextReadModel;
  themeMode: CustomerThemeMode;
};

const CustomerPortalContext = createContext<CustomerPortalContextValue | null>(null);

export function CustomerPortalProvider({ children, value }: { children: React.ReactNode; value: CustomerPortalContextValue }) {
  return <CustomerPortalContext.Provider value={value}>{children}</CustomerPortalContext.Provider>;
}

export function useCustomerPortalContext(): CustomerPortalContextValue {
  const context = useContext(CustomerPortalContext);

  if (!context) {
    throw new Error("useCustomerPortalContext must be used within CustomerPortalProvider.");
  }

  return context;
}
